import { isPlgOnboardingProvider } from '../../services/tenant/plgOnboarding';
import { collect } from 'collect.js';
import { FeatureFlags, PaymentGateway } from '../../services/exports/Constants';

/**
 * Pure onboarding-verification rules extracted from useVerification.jsx.
 *
 * These cover the field-completeness predicates (which depend on a `getValue`
 * accessor over the merchant's business profile) and the step-state derivation
 * (completion, progress, next active step). The hook stays responsible for
 * wiring Redux/context state and rendering each step's UI; all the decision
 * logic lives here so it can be unit-tested without React.
 *
 * See tests/hooks/verification/verificationRules.test.js.
 */

/**
 * Step required-ness must fail CLOSED: while the provider payload (or its
 * enabled_feature_flags) has not arrived, treat merchant billing as ON so the
 * Subscription/PaymentInfo steps stay required and onboarding cannot
 * auto-complete — and irreversibly stamp onboarding_complete_at — during a
 * payload gap (frontend deployed ahead of the backend, or
 * currentServiceProvider lagging currentCompany). Hiding money UI fails open
 * instead (see HasMerchantBillingGuard); the asymmetry is deliberate.
 */
export function resolveMerchantBillingForSteps(serviceProvider) {
  const flags = serviceProvider?.enabled_feature_flags;

  return Array.isArray(flags) ? flags.includes(FeatureFlags.MerchantBilling) : true;
}

const BASE_PERSONAL_FIELDS = [
  'owner_first_name',
  'owner_last_name',
  'owner_dob',
  'owner_address',
  'owner_city',
  'owner_state',
  'owner_country',
  'owner_zip_code',
];

/** Personal fields required for onboarding — US legal entities also need the owner id number. */
export function getRequiredPersonalFields(legalCountry) {
  return legalCountry === 'US' ? [...BASE_PERSONAL_FIELDS, 'owner_id_number'] : BASE_PERSONAL_FIELDS;
}

/**
 * Get Started's KYC steps — Store details, then Business details typed from scratch (PROD-558,
 * PROD-538), the owner's personal information and the document slots — are for Foodamigos'
 * own restaurants in Germany only: the legal forms and documents are German ones, a partner's
 * staff enter the details themselves, and a PLG partner's restaurants have their own self-serve
 * Business details step, which never writes the stamps these steps wait for (Bohdan). Everyone
 * else keeps the combined "Add your store details" step and its field check until theirs are
 * defined (Nishita). The restaurant's own country decides, not the legal country typed into that
 * step. Staff run these steps: Get Started is not opened to owners (Nishita and Artur, 20 Oct).
 */
export function runsGermanKycSteps(company, serviceProvider) {
  return company?.country === 'DE' && Boolean(serviceProvider?.is_master) && !isPlgOnboardingProvider(serviceProvider);
}

/**
 * The restaurant's name and address are always filled at creation, so no field check can tell
 * whether anyone looked at them. The step is finished once it was saved, which stamps
 * the merchant (one per restaurant, unlike the business profile a group's locations share).
 * Restaurants that existed before the step was introduced were stamped by its migration.
 */
export function checkStoreDetails(merchant) {
  return Boolean(merchant?.store_details_confirmed_at);
}

/**
 * A restaurant created since PROD-538 has the owner type the business details and pick the
 * legal form, so the fields staff filled at creation say nothing: the step is finished once
 * the owner saved it, which stamps the restaurant's own profile (never inherited — a group
 * saved for all its restaurants stamps each). Restaurants created before keep the field
 * check, so they are not sent back. An owner who comes back and saves the step finishes it
 * either way: the form asks for less than that check reads, a registered name for a GbR or
 * GmbH only.
 */
export function checkBusinessInfo(getValue, { ownProfile, requiresConfirmation = false } = {}) {
  if (ownProfile?.business_details_completed_at) {
    return true;
  }

  if (requiresConfirmation) {
    return false;
  }

  return (
    Boolean(getValue('legal_name')) &&
    Boolean(getValue('legal_country')) &&
    Boolean(getValue('legal_state')) &&
    Boolean(getValue('legal_city')) &&
    Boolean(getValue('legal_address')) &&
    Boolean(getValue('legal_zip_code'))
  );
}

/**
 * Same as Business details (PROD-559): a restaurant created since asks the owner to type their
 * own personal information, so whatever staff filled in under My info says nothing — the step
 * is finished once the owner saved it, which stamps the restaurant's own profile. Restaurants
 * created before keep the field check, so they are not sent back.
 */
export function checkPersonalInfo(getValue, legalCountry, { ownProfile, requiresConfirmation = false } = {}) {
  if (ownProfile?.personal_info_confirmed_at) {
    return true;
  }

  if (requiresConfirmation) {
    return false;
  }

  return getRequiredPersonalFields(legalCountry).reduce((result, field) => result && Boolean(getValue(field)), true);
}

export function checkShippingInfo(getValue) {
  return (
    Boolean(getValue('shipping_company_name')) &&
    Boolean(getValue('shipping_salutation')) &&
    Boolean(getValue('shipping_first_name')) &&
    Boolean(getValue('shipping_last_name')) &&
    Boolean(getValue('shipping_address')) &&
    Boolean(getValue('shipping_zip_code')) &&
    Boolean(getValue('shipping_city')) &&
    Boolean(getValue('shipping_phone_number'))
  );
}

/**
 * Bank details are complete when the account number is present. Stripe additionally
 * requires the externally-stored stripe bank account id; Adyen does not.
 */
export function checkBankDetails(getValue, { paymentGateway, stripeBankAccountId }) {
  if (paymentGateway === PaymentGateway.Adyen) {
    return Boolean(getValue('bank_account_number'));
  }

  return Boolean(getValue('bank_account_number')) && Boolean(stripeBankAccountId);
}

export function checkTaxDetails(getValue) {
  return Boolean(getValue('tax_id'));
}

/**
 * Get Started's document slots (PROD-544) are finished once no required slot is missing, as the
 * backend reports it for the legal form — a required ID slot needs the owner's own ID. A
 * restaurant that already went through the two old document steps when the slots shipped needs
 * nothing more (Nishita: no backfill), even when what it uploaded there fills no slot; the backend
 * marks those with `requires_document_slots: false`. Until the slots arrive the step reads as
 * unfinished, so onboarding never completes on a missing payload.
 */
export function checkDocumentSlots(slots, merchant) {
  if (merchant?.requires_document_slots === false) {
    return true;
  }

  return Array.isArray(slots) && slots.length > 0 && slots.every((slot) => !slot.is_missing);
}

/** Delivery structure is captured once at least one delivery mode has been selected for the location. */
export function checkDeliveryStructure(deliveryModes) {
  return Array.isArray(deliveryModes) && deliveryModes.length > 0;
}

/** A step is done when finished, or skippable and explicitly skipped by the merchant. */
export function isStepFinished(step, skippedSteps) {
  return step.is_finished || (step?.is_skippable && collect(skippedSteps).contains(step.id));
}

/** Onboarding is complete when already stamped, or every required step is finished. */
export function isOnboardingComplete(steps, skippedSteps, onboardingCompleteAt) {
  return (
    !!onboardingCompleteAt ||
    collect(steps)
      .where('is_required', true)
      .every((item) => isStepFinished(item, skippedSteps))
  );
}

export function getRequiredSteps(steps) {
  return collect(steps).where('is_required').toArray();
}

/** First required, not-yet-finished step (the one the wizard should land on). */
export function findLastActiveStep(steps, skippedSteps) {
  return collect(steps).first((item) => item.is_required && !isStepFinished(item, skippedSteps));
}

export function getRemainingStepsCount(steps, skippedSteps) {
  return collect(steps)
    .filter((item) => item.is_required && !isStepFinished(item, skippedSteps))
    .count();
}

export function getProgress(steps, skippedSteps) {
  return {
    total: getRequiredSteps(steps).length,
    value: collect(steps).search((item) => item.id === findLastActiveStep(steps, skippedSteps)?.id),
  };
}
